<template>
  <b-row class="m-3">
    <b-col md="8" offset-md="2" lg="6" offset-lg="3">
      <!-- Invalid / expired token -->
      <b-card v-if="tokenError" header="Activation Link Invalid" header-bg-variant="danger" header-text-variant="white" bg-variant="light">
        <p class="mb-3">
          This request verification has expired or has already been used. A verification token may only be used once within 48 hours.
          Please resubmit your request.
        </p>
        <div class="d-flex justify-content-between">
          <a :href="memberPortalUrl" class="btn btn-sm btn-secondary">Cancel</a>
          <a :href="`${memberPortalUrl}/app/account/create`" class="btn btn-sm btn-primary">Resubmit Request</a>
        </div>
      </b-card>

      <!-- Loading token verification -->
      <b-card v-else-if="verifying" header="Verifying…" header-bg-variant="dark" header-text-variant="white" bg-variant="light">
        <div class="text-center py-3">
          <b-spinner />
        </div>
      </b-card>

      <!-- Set password form -->
      <b-card v-else header="Activate Your Account" header-bg-variant="dark" header-text-variant="white" bg-variant="light">
        <!-- Success banner -->
        <b-alert show variant="success" class="mb-3">
          <b-icon icon="check-circle-fill" class="mr-2" />
          Thank you for verifying your account using email address
          <strong>{{ email }}</strong>
          . Please select a password:
        </b-alert>

        <!-- Email mismatch warning -->
        <b-alert v-if="emailMismatch" show variant="warning" class="mb-3">
          <b-icon icon="exclamation-triangle-fill" class="mr-2" />
          This email address does not match your UBC member information. You may check the box below to update your information.
          <div class="mt-2">
            <b-form-checkbox v-model="updateMemberInfo">
              Update my UBC Member information to use
              <strong>{{ email }}</strong>
            </b-form-checkbox>
          </div>
        </b-alert>

        <!-- Member info update fields -->
        <template v-if="updateMemberInfo">
          <b-form-group label="Email Address:" label-cols-sm="4" label-align-sm="right">
            <b-input v-model="memberEmail" type="email" :state="validated('memberEmail')" placeholder="Email Address" />
            <b-form-invalid-feedback>Please enter a valid email address</b-form-invalid-feedback>
          </b-form-group>

        </template>

        <b-form @submit.prevent="onSubmit">
          <password-and-confirm-fields
            :password.sync="password"
            :confirm.sync="passwordConfirm"
            :password-state="validated('password')"
            :confirm-state="validated('passwordConfirm')"
            :password-feedback="passwordFeedback || ''"
          />

          <div class="d-flex justify-content-between align-items-center mt-3">
            <b-button size="sm" variant="secondary" :disabled="loading" :to="{ name: 'login' }">Cancel</b-button>
            <b-button size="sm" type="submit" variant="success" :disabled="loading || submitDisabled">
              <b-spinner v-if="loading" small class="mr-1" />
              Activate My Account
            </b-button>
          </div>
        </b-form>
      </b-card>
    </b-col>
  </b-row>
</template>
<script>
import { Vue, Component } from 'vue-property-decorator';
import memberDao from '@/dao/member_dao';
import { emailValidator } from '@/util/validator';
import passwordMixin from '@/mixins/password_mixin';
import PasswordAndConfirmFields from '@/components/PasswordAndConfirmFields';
import { renderErrorBody } from '@/util/error';

@Component({
  mixins: [passwordMixin],
  components: { PasswordAndConfirmFields },
  metaInfo: {
    title: 'Member Portal - Activate Account'
  }
})
export default class AccountActivation extends Vue {
  verifying = true;
  tokenError = null;
  loading = false;

  username = '';
  token = '';
  email = '';
  emailMismatch = false;

  updateMemberInfo = false;
  memberEmail = '';
  password = '';
  passwordConfirm = '';

  get memberPortalUrl() {
    return this.$store.getters.meta?.memberPortalUrl || 'https://member-portal.carpenters.org';
  }

  get submitDisabled() {
    const emailOk = !this.updateMemberInfo || this.validated('memberEmail') === true;
    return !this.validated('password') || !this.validated('passwordConfirm') || !emailOk;
  }

  validated(field) {
    switch (field) {
      case 'password':
        return !this.passwordValue.length ? undefined : !this.passwordFeedback;
      case 'passwordConfirm':
        return !this.passwordValue ? undefined : (this.passwordConfirm || '').trim().length > 0 && this.password === this.passwordConfirm;
      case 'memberEmail': {
        const v = (this.memberEmail || '').trim();
        return !v.length ? false : emailValidator(v);
      }
      default:
        return true;
    }
  }

  async mounted() {
    try {
      this.token = this.$route.query.token || '';
      if (!this.token) {
        throw new Error('No activation token was found in the link. Please check your email and try again.');
      }
      const { username, email, email_mismatch } = await memberDao.verifyActivationToken(this.token);
      this.username = username;
      this.email = email || '';
      this.emailMismatch = !!email_mismatch;
      this.memberEmail = this.email;
    } catch (error) {
      this.tokenError = (error && error.message) || 'This activation link is invalid or has expired.';
    } finally {
      this.verifying = false;
    }
  }

  async onSubmit() {
    if (this.submitDisabled) return;
    try {
      this.loading = true;
      await memberDao.activateAccount({
        token: this.token,
        password: this.password,
        update_member_info: this.updateMemberInfo,
        member_email: this.updateMemberInfo ? this.memberEmail : null
      });
      const params = new URLSearchParams({ registered: '1' });
      if (this.updateMemberInfo && this.memberEmail) {
        params.set('email_updated', '1');
      }
      window.location.href = `${this.memberPortalUrl}/?${params.toString()}`;
    } catch (error) {
      const body = renderErrorBody(this.$createElement, (error && error.message) || 'An unknown error occurred, please try again.');
      this.$bvModal.msgBoxOk(body, {
        title: 'Activation Failed',
        okTitle: 'OK',
        okVariant: 'danger',
        centered: true,
        headerBgVariant: 'danger',
        headerTextVariant: 'white'
      });
    } finally {
      this.loading = false;
    }
  }
}
</script>
<style scoped></style>
